import { EventEmitter, Injectable } from '@angular/core';

// FIX ME
//const values = require('lodash/flattenDeep');

// import flattenDeep from 'lodash';

import * as cee from './Common/CeeEnvisionWebComponents';

import { C3Service } from './c3.service';
import { ModelService } from './model.service';
import { timer } from 'rxjs';
import { takeWhile } from 'rxjs/operators';

export enum AnimationType {
  State,
  ParticleTrace,
  ModeShape,
}

@Injectable()
export class AnimationService {
  private _c3Service: C3Service;
  private _stateFrameHandler: StateFrameHandler;
  private _stateAnimationHandler!: StateAnimationHandler;
  private _animationFrameHandler: AnimationFrameHandler;
  private _particleTraceAnimationHandler!: ParticleTraceAnimationHandler;
  private _modeShapeAnimationHandler!: ModeShapeAnimationHandler;
  private _animationHandlers: { [type: number]: AnimationHandler } = {};
  private _curFrameHandler: FrameHandler;
  private _curAnimationHandler!: AnimationHandler;

  private modelIds: Set<number> = new Set();

  public isAnimationTaskAlive = false;

  public get stateAnimationHandler() {
    return this._stateAnimationHandler;
  }
  public get particleTraceAnimationHandler() {
    return this._particleTraceAnimationHandler;
  }
  public get modeShapeAnimationHandler() {
    return this._modeShapeAnimationHandler;
  }
  public get curAnimationType() {
    return this._curAnimationHandler ? this._curAnimationHandler.type : undefined;
  }
  // public get animationIsRunning() { return this.animation.isAnyAnimationRunning(); }

  private get animation() {
    return this._c3Service.model.animation;
  }

  public constructor(c3Service: C3Service, modelService: ModelService) {
    this._c3Service = c3Service;
    this._stateFrameHandler = new StateFrameHandler(c3Service);
    this._animationFrameHandler = new AnimationFrameHandler(c3Service);
    this._curFrameHandler = this._stateFrameHandler;

    modelService.modelOpened.subscribe(() => {
      this.setHandlers();
      Object.entries(this._c3Service._model).forEach((model: any, id: number) => {
        this.modelIds.add(id);
      });
    });

    if (modelService.modelOpen) {
      this.setHandlers();
    }
  }

  // public play() {
  //     // if (!this._curAnimationHandler) {
  //     //     this._curAnimationHandler = this.getDefaultAnimation();
  //     //     if (this._curAnimationHandler) {
  //     //         this._curAnimationHandler.apply();
  //     //         this._curAnimationHandler.play();
  //     //     }
  //     // } else {
  //     //     this._curAnimationHandler.play();
  //     // }
  // }

  public pause() {
    if (this._curAnimationHandler) {
      this._curAnimationHandler.pause();
    }
  }

  public play(start: boolean) {
    this.isAnimationTaskAlive = start;

    Object.entries(this._c3Service._model).forEach((model: any, id: number) => {
      this._curFrameHandler.skipFirst(id);

      const size =
        this._c3Service._model[id].modelSpec['m_ownerModelConfig']['m_modelSpec']['m_ownerModelConfig'][
          'm_modelDirectory'
        ]['stateInfoArray'].length;

      let index = 0;

      timer(500, 500)
        .pipe(takeWhile(() => this.isAnimationTaskAlive))
        .subscribe(() => {
          this._curFrameHandler.skipNext(id);
          index++;

          if (size === index) {
            this.isAnimationTaskAlive = false;
          }
        });
    });
  }

  public stop() {
    this.isAnimationTaskAlive = false;
  }

  public skipFirst() {
    this.modelIds.forEach((id) => {
      this._curFrameHandler.skipFirst(id);
    });
  }

  public skipPrevious() {
    this.modelIds.forEach((id) => {
      this._curFrameHandler.skipPrevious(id);
    });
  }

  public skipNext() {
    this.modelIds.forEach((id) => {
      this._curFrameHandler.skipNext(id);
    });
  }

  public skipLast(id: number) {
    this.modelIds.forEach((id) => {
      this._curFrameHandler.skipLast(id);
    });
  }

  public slower() {
    this.animation.targetFrameRate = Math.max(this.animation.targetFrameRate - 5, 0);
  }

  public faster() {
    this.animation.targetFrameRate += 5;
  }

  public getHandler(type: AnimationType) {
    return this._animationHandlers[type];
  }

  public removeAnimation() {
    if (this._curAnimationHandler) {
      this.pause();
      this._curAnimationHandler = null as any;
    }

    this._curFrameHandler = this._stateFrameHandler;
    this._stateFrameHandler.skipFirst(1);
  }

  private flatten(ary: any): any {
    var ret: any = [];
    for (var i = 0; i < ary.length; i++) {
      if (Array.isArray(ary[i])) {
        ret = ret.concat(this.flatten(ary[i]));
      } else {
        ret.push(ary[i]);
      }
    }
    return ret;
  }

  private setHandlers() {
    this._stateAnimationHandler = new StateAnimationHandler(this._c3Service.model, this._c3Service);
    this._particleTraceAnimationHandler = new ParticleTraceAnimationHandler(this._c3Service.model);
    this._modeShapeAnimationHandler = new ModeShapeAnimationHandler(this._c3Service.model);
    this._animationHandlers[AnimationType.State] = this._stateAnimationHandler;
    this._animationHandlers[AnimationType.ParticleTrace] = this._particleTraceAnimationHandler;
    this._animationHandlers[AnimationType.ModeShape] = this._modeShapeAnimationHandler;

    const flat = this.flatten(this._animationHandlers);

    flat.forEach((handler: AnimationHandler) =>
      handler.onApply.subscribe((h: any) => {
        this._curAnimationHandler = h;
        this._curFrameHandler = this._animationFrameHandler;
      })
    );
  }

  private getDefaultAnimation() {
    const flat = this.flatten(this._animationHandlers);
    return flat.find((h: any) => h.enabled);
  }
}

interface FrameHandler {
  skipFirst(id: number): void;
  skipPrevious(id: number): void;
  skipNext(id: number): void;
  skipLast(id: number): void;
}

class StateFrameHandler implements FrameHandler {
  private _c3Service: C3Service;

  private get modelSpec() {
    return this._c3Service.model.modelSpec;
  }
  private get modelDir() {
    return this._c3Service.model.modelDirectory;
  }

  public constructor(c3Service: C3Service) {
    this._c3Service = c3Service;
  }

  public skipFirst(id = 0) {
    this._c3Service._model[id].modelSpec.stateIdArray = [
      this._c3Service._model[id].modelDirectory.stateInfoArray[0].id,
    ];
  }

  public skipPrevious(id: number = 0) {
    const curId = this._c3Service._model[id].modelSpec.stateIdArray[0];
    const curIndex = this._c3Service._model[id].modelDirectory.stateInfoArray.findIndex((s: any) => s.id === curId);
    if (curIndex > 0) {
      this._c3Service._model[id].modelSpec.stateIdArray = [
        this._c3Service._model[id].modelDirectory.stateInfoArray[curIndex - 1].id,
      ];
    }
  }

  public skipNext(id: any = 0) {
    id = id.toString();
    const curId = this._c3Service._model[id].modelSpec.stateIdArray[0];
    const curIndex = this._c3Service._model[id].modelDirectory.stateInfoArray.findIndex((s: any) => s.id === curId);
    if (curIndex < this._c3Service._model[id].modelDirectory.stateInfoArray.length - 1) {
      this._c3Service._model[id].modelSpec.stateIdArray = [
        this._c3Service._model[id].modelDirectory.stateInfoArray[curIndex + 1].id,
      ];
    }
  }

  public skipLast(id: number) {
    const index = this._c3Service._model[id].modelDirectory.stateInfoArray.length - 1;
    this._c3Service._model[id].modelSpec.stateIdArray = [
      this._c3Service._model[id].modelDirectory.stateInfoArray[index].id,
    ];
  }
}

class AnimationFrameHandler implements FrameHandler {
  private _c3Service: C3Service;

  private get animation() {
    return this._c3Service.model.animation;
  }

  public constructor(c3Service: C3Service) {
    this._c3Service = c3Service;
  }

  public skipFirst() {
    this.animation.setFirstFrame();
  }

  public skipPrevious() {
    this.animation.setPreviousFrame();
  }

  public skipNext() {
    this.animation.setNextFrame();
  }

  public skipLast() {
    this.animation.setLastFrame();
  }
}

abstract class AnimationHandler {
  public onApply = new EventEmitter<AnimationHandler>();

  public abstract get type(): AnimationType;
  public abstract get enabled(): boolean;

  public apply() {
    this.doApply();
    this.onApply.next(this);
  }

  public abstract play(): void;
  public abstract pause(): void;

  protected abstract doApply(): void;
}

class StateAnimationHandler extends AnimationHandler {
  public from = 0;
  public to = 0;
  public skipBy = 1;

  private _model: cee.ug.RemoteModel;

  public get type() {
    return AnimationType.State;
  }

  public get enabled() {
    return this.c3Service._model['0'].modelDirectory.stateInfoArray.length > 1;
  }

  public constructor(model: cee.ug.RemoteModel, private c3Service: C3Service) {
    super();
    this._model = model;

    setTimeout(() => {
      const states = this.c3Service._model['0'].modelDirectory.stateInfoArray;
      this.from = states[0].id;
      this.to = states[states.length - 1].id;
    }, 3000);
  }

  public play() {
    this._model.animation.runAnimation(true);
  }

  public pause() {
    this._model.animation.runAnimation(false);
  }

  protected doApply() {
    const stateIds: number[] = [];
    for (let i = this.from; i <= this.to; i += this.skipBy) {
      stateIds.push(i);
    }

    this._model.modelSpec.stateIdArray = stateIds;
  }
}

class ParticleTraceAnimationHandler extends AnimationHandler {
  public numSteps = 300;
  private _model: cee.ug.RemoteModel;

  public get type() {
    return AnimationType.ParticleTrace;
  }

  public get enabled() {
    return this._model.getParticleTraceGroupArray().length > 0;
  }

  public constructor(model: cee.ug.RemoteModel) {
    super();
    this._model = model;
  }

  public play() {
    this._model.animation.runParticleTraceAnimation(true);
  }

  public pause() {
    this._model.animation.runParticleTraceAnimation(false);
  }

  protected doApply() {
    this._model.animation.particleTraceAnimationStepCount = this.numSteps;
  }
}

class ModeShapeAnimationHandler extends AnimationHandler {
  public modeShapeAnimationType: cee.ug.ModeShapeAnimationType = cee.ug.ModeShapeAnimationType.FULL;
  public frameCount = 30;

  private _model: cee.ug.RemoteModel;

  public get type() {
    return AnimationType.ModeShape;
  }

  public get enabled() {
    return this._model.modelSpec.displacementResultId > -1;
  }

  public constructor(model: cee.ug.RemoteModel) {
    super();
    this._model = model;
  }

  public play() {
    this._model.animation.runAnimation(true);
  }

  public pause() {
    this._model.animation.runAnimation(false);
  }

  protected doApply() {
    const modelSpec = this._model.modelSpec;
    modelSpec.modeShapeAnimationType = this.modeShapeAnimationType;
    modelSpec.modeShapeFrameCount = this.frameCount;

    if (modelSpec.stateIdArray.length > 1) {
      modelSpec.stateIdArray = [modelSpec.stateIdArray[0]];
    }
  }
}
